import { Component } from '@theme/component';
import {
  supportsViewTransitions,
  startViewTransition,
  onAnimationEnd,
  prefersReducedMotion,
  debounce,
  preloadImage,
  isLowPowerDevice,
  isMobileBreakpoint,
} from '@theme/utilities';
import { scrollIntoView } from '@theme/scrolling';
import { ZoomMediaSelectedEvent } from '@theme/events';
import { DialogCloseEvent } from '@theme/dialog';

/** The longest the dialog waits for a photo to be ready before opening anyway, in milliseconds. */
const OPEN_WAIT = 600;

/** The longest a zoom waits for the full-resolution photo before zooming the smaller one, in milliseconds. */
const ZOOM_WAIT = 1500;

/**
 * A custom element that renders a zoom dialog.
 *
 * @typedef {object} Refs
 * @property {HTMLDialogElement} dialog - The dialog element.
 * @property {HTMLElement[]} media - The media elements.
 * @property {HTMLElement} thumbnails - The thumbnails elements.
 * @property {HTMLElement} [track] - The list that holds the media and scrolls between them.
 * @property {HTMLElement} [counter] - Shows the position of the current media, e.g. "3 / 11".
 * @property {HTMLElement} [caption] - Shows the caption of the current media.
 * @property {HTMLButtonElement} [previousButton] - Goes to the previous media.
 * @property {HTMLButtonElement} [nextButton] - Goes to the next media.
 *
 * @extends Component<Refs>
 */
export class ZoomDialog extends Component {
  requiredRefs = ['dialog', 'media', 'thumbnails'];

  #highResImagesLoaded = /** @type {Set<string>} */ (new Set());

  /** Full-resolution photos being loaded, by URL. */
  #highResLoads = /** @type {Map<string, Promise<void>>} */ (new Map());

  /** The index of the media currently shown. */
  #index = 0;

  get #scroller() {
    return this.refs.track ?? this.refs.dialog;
  }

  connectedCallback() {
    super.connectedCallback();
    this.#scroller.addEventListener('scroll', this.handleScroll);
    this.#scroller.addEventListener('pointermove', this.#handlePointerMove);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#scroller.removeEventListener('scroll', this.handleScroll);
    this.#scroller.removeEventListener('pointermove', this.#handlePointerMove);
  }

  /**
   * Shows which media is current: the selected thumbnail, the counter, the caption and the state of the arrows.
   * Also resets any zoom, so every image starts unzoomed.
   * @param {number} index - The index of the current media.
   */
  #setCurrent(index) {
    const { media, thumbnails, counter, caption, previousButton, nextButton } = this.refs;

    this.#index = index;

    Array.from(thumbnails.querySelectorAll('button')).forEach((button, i) => {
      button.setAttribute('aria-selected', `${i === index}`);
    });

    if (counter) {
      counter.textContent = (counter.dataset.template ?? '').replace('[index]', `${index + 1}`);
    }

    if (caption) {
      caption.textContent = media[index]?.dataset.caption ?? '';
    }

    if (previousButton) previousButton.disabled = index <= 0;
    if (nextButton) nextButton.disabled = index >= media.length - 1;

    for (const item of media) this.#setZoomed(item, false);

    // Get the photos either side ready, so the arrows show them straight away
    this.warmImage(index - 1);
    this.warmImage(index + 1);
  }

  /** Shows the next media. */
  next() {
    this.selectThumbnail(Math.min(this.#index + 1, this.refs.media.length - 1), { behavior: scrollBehavior() });
  }

  /** Shows the previous media. */
  previous() {
    this.selectThumbnail(Math.max(this.#index - 1, 0), { behavior: scrollBehavior() });
  }

  /**
   * Handles a click on a media item. On phones it closes the dialog; zooming there is done by pinching or
   * double-tapping. On larger screens a click on the image zooms in on the clicked point (moving the pointer then
   * pans), a second click zooms out, and a click next to the image closes the dialog.
   * @param {MouseEvent} event - The click event.
   */
  async handleMediaClick(event) {
    if (isMobileBreakpoint()) return this.close();

    const item = event.target instanceof HTMLElement ? event.target.closest('li') : null;
    const frame = item?.querySelector('.product-media');
    if (!item || !frame) return this.close();

    const rect = frame.getBoundingClientRect();
    const isOnImage =
      event.clientX >= rect.left &&
      event.clientX <= rect.right &&
      event.clientY >= rect.top &&
      event.clientY <= rect.bottom;

    if (!isOnImage) return this.close();

    if (item.classList.contains('is-zoomed')) return this.#setZoomed(item, false);

    // Zoom into the full-resolution photo, not an enlarged blurry one: wait for it (briefly) first
    setZoomOrigin(item, frame, event);
    const index = this.#index;
    item.classList.add('is-zoom-loading');
    await Promise.race([this.loadHighResolutionImage(item), wait(ZOOM_WAIT)]);
    item.classList.remove('is-zoom-loading');

    if (this.#index !== index || !this.refs.dialog.open) return;
    // A freshly swapped-in photo needs its unzoomed style computed first, or it jumps instead of zooming smoothly
    item.querySelector('img.product-media__image')?.getBoundingClientRect();
    this.#setZoomed(item, true);
  }

  /**
   * Starts loading the photo at `index` at the size the dialog shows it, and resolves once it's decoded, or after
   * a short wait so nothing ever stalls on a slow connection.
   * @param {number} index - The index of the media.
   * @returns {Promise<void>}
   */
  warmImage(index) {
    const image = this.refs.media[index]?.querySelector('img.product-media__image');
    if (!(image instanceof HTMLImageElement)) return Promise.resolve();

    image.loading = 'eager';
    return Promise.race([image.decode().catch(() => {}), wait(OPEN_WAIT)]);
  }

  /**
   * @param {HTMLElement} item - The media item.
   * @param {boolean} zoomed - Whether the item is zoomed in.
   */
  #setZoomed(item, zoomed) {
    item.classList.toggle('is-zoomed', zoomed);
  }

  /**
   * Pans a zoomed image by following the pointer.
   * @param {PointerEvent} event - The pointer event.
   */
  #handlePointerMove = (event) => {
    if (event.pointerType === 'touch' || !(event.target instanceof Element)) return;

    const item = event.target.closest('li.is-zoomed');
    const frame = item?.querySelector('.product-media');
    if (!(item instanceof HTMLElement) || !frame) return;

    setZoomOrigin(item, frame, event);
  };

  /**
   * Opens the zoom dialog.
   *
   * @param {number} index - The index of the media to zoom.
   * @param {PointerEvent} event - The pointer event.
   */
  async open(index, event) {
    event.preventDefault();

    // Phones swipe through the photos in the gallery itself; there is no fullscreen preview
    if (isMobileBreakpoint()) return;

    const { dialog, media, thumbnails } = this.refs;
    const targetImage = media[index];
    const targetThumbnail = thumbnails.children[index];

    this.#setCurrent(index);

    // Open on a sharp photo: without this the animation catches the photo before it has loaded
    await this.warmImage(index);

    const open = () => {
      dialog.showModal();

      for (const target of [targetThumbnail, targetImage]) {
        target?.scrollIntoView({ behavior: 'instant' });
      }
    };

    /** @type {HTMLElement | null} */
    const sourceImage = event.target instanceof Element ? event.target.closest('li,slideshow-slide') : null;

    if (!supportsViewTransitions() || isLowPowerDevice() || !sourceImage || !targetImage) return open();

    // Animate the image itself, so a caption under it doesn't get stretched into the transition
    const sourceFrame = mediaFrame(sourceImage);
    const targetFrame = mediaFrame(targetImage);

    const itemTransitionName = `gallery-item-open`;
    sourceFrame.style.setProperty('view-transition-name', itemTransitionName);

    const focalPoint = sourceImage.dataset.focalPoint;
    if (focalPoint) {
      document.documentElement.style.setProperty('--gallery-media-focal-point', focalPoint);
    }

    await startViewTransition(() => {
      open();
      sourceFrame.style.removeProperty('view-transition-name');
      targetFrame.style.setProperty('view-transition-name', itemTransitionName);
    });

    document.documentElement.style.removeProperty('--gallery-media-focal-point');
    targetFrame.style.removeProperty('view-transition-name');

    this.selectThumbnail(index, { behavior: 'instant' });
  }

  /**
   * Loads the full-resolution version of a photo and swaps it in once it's decoded, so the swap is invisible.
   * @param {HTMLElement} mediaContainer - The media container element
   * @returns {Promise<void>} Resolves once the full-resolution photo is shown (or can't be loaded).
   */
  loadHighResolutionImage(mediaContainer) {
    if (!mediaContainer.classList.contains('product-media-container--image')) return Promise.resolve();

    const image = mediaContainer.querySelector('img.product-media__image');
    if (!image || !(image instanceof HTMLImageElement)) return Promise.resolve();

    const highResolutionUrl = image.getAttribute('data_max_resolution');
    if (!highResolutionUrl || this.#highResImagesLoaded.has(highResolutionUrl)) return Promise.resolve();

    const pending = this.#highResLoads.get(highResolutionUrl);
    if (pending) return pending;

    preloadImage(highResolutionUrl);

    const newImage = new Image();
    newImage.className = image.className;
    newImage.alt = image.alt;
    newImage.setAttribute('data_max_resolution', highResolutionUrl);
    newImage.setAttribute('ref', 'image');
    newImage.src = highResolutionUrl;

    const load = newImage
      .decode()
      .then(() => {
        // Swap the element that is in the item now, in case it changed while loading
        const current = mediaContainer.querySelector('img.product-media__image');
        current?.replaceWith(newImage);
        this.#highResImagesLoaded.add(highResolutionUrl);
      })
      .catch(() => {})
      .finally(() => this.#highResLoads.delete(highResolutionUrl));

    this.#highResLoads.set(highResolutionUrl, load);
    return load;
  }

  /**
   * Handles the scroll event of the dialog, which is used to update the active thumbnail when the corresponding image is visible in the main view.
   * @param {Event} event - The scroll event.
   */
  handleScroll = debounce(async () => {
    const { media, thumbnails } = this.refs;

    const mostVisibleElement = await getMostVisibleElement(media);
    const activeIndex = media.indexOf(mostVisibleElement);
    const targetThumbnail = thumbnails.children[activeIndex];

    if (activeIndex < 0) return;

    if (activeIndex !== this.#index) {
      this.#setCurrent(activeIndex);
      if (targetThumbnail instanceof HTMLElement) {
        scrollIntoView(targetThumbnail, {
          ancestor: thumbnails,
          behavior: 'instant',
          block: 'center',
          inline: 'center',
        });
      }
    }

    // Phones pinch-zoom the photo on screen, so give them the full resolution; larger screens load it when zooming
    if (isMobileBreakpoint()) this.loadHighResolutionImage(mostVisibleElement);
    this.dispatchEvent(new ZoomMediaSelectedEvent(activeIndex));
  }, 50);

  /**
   * Closes the zoom dialog.
   */
  async close() {
    const { dialog, media } = this.refs;

    if (!supportsViewTransitions() || isLowPowerDevice()) return this.closeDialog();

    // Find the most visible image using IntersectionObserver
    const mostVisibleElement = await getMostVisibleElement(media);

    // Get the index and set up transition
    const activeIndex = media.indexOf(mostVisibleElement);
    const itemTransitionName = `gallery-item-close`;

    const mediaGallery = /** @type {import('./media-gallery').MediaGallery | undefined} */ (
      this.closest('media-gallery')
    );

    const slideshowActive = mediaGallery?.presentation === 'carousel';

    const slide = slideshowActive ? mediaGallery.slideshow?.slides?.[activeIndex] : mediaGallery?.media?.[activeIndex];

    if (!slide) return this.closeDialog();
    const focalPoint = slide.dataset.focalPoint;
    if (focalPoint) {
      document.documentElement.style.setProperty('--gallery-media-focal-point', focalPoint);
    }

    const sourceFrame = mediaFrame(mostVisibleElement);
    const targetFrame = mediaFrame(slide);

    for (const item of media) this.#setZoomed(item, false);
    dialog.classList.add('dialog--closed');

    await onAnimationEnd(this.refs.thumbnails);

    sourceFrame.style.setProperty('view-transition-name', itemTransitionName);

    await startViewTransition(() => {
      sourceFrame.style.removeProperty('view-transition-name');
      targetFrame.style.setProperty('view-transition-name', itemTransitionName);
      this.closeDialog();
    });

    targetFrame.style.removeProperty('view-transition-name');
    dialog.classList.remove('dialog--closed');
    document.documentElement.style.removeProperty('--gallery-media-focal-point');
  }

  closeDialog() {
    const { dialog } = this.refs;
    dialog.close();
    window.dispatchEvent(new DialogCloseEvent());
  }

  /**
   * Closes the dialog when the user presses the escape key.
   *
   * @param {KeyboardEvent} event - The keyboard event.
   */
  handleKeyDown(event) {
    if (event.key === 'Escape') {
      event.preventDefault();
      this.close();
    } else if (event.key === 'ArrowRight') {
      event.preventDefault();
      this.next();
    } else if (event.key === 'ArrowLeft') {
      event.preventDefault();
      this.previous();
    }
  }

  /**
   * Handles the click event of a thumbnail.
   * @param {number} index - The index of the thumbnail to select.
   */
  async handleThumbnailClick(index) {
    this.selectThumbnail(index, { behavior: scrollBehavior() });
  }

  /**
   * Handles the pointer enter event of a thumbnail.
   * @param {number} index - The index of the thumbnail whose photo to get ready.
   */
  async handleThumbnailPointerEnter(index) {
    this.warmImage(index);
  }

  /**
   * Handles the selection of a thumbnail.
   * @param {number} index - The index of the thumbnail to select.
   * @param {Object} options - The options for the selection.
   * @param {ScrollBehavior} options.behavior - The behavior of the scroll.
   */
  async selectThumbnail(index, options = { behavior: 'smooth' }) {
    if (!this.refs.thumbnails || !this.refs.thumbnails.children.length) return;

    // Guard if invalid
    if (isNaN(index) || index < 0 || index >= this.refs.thumbnails.children.length) return;

    const { media, thumbnails } = this.refs;
    const targetThumbnail = thumbnails.children[index];

    if (!targetThumbnail || !(targetThumbnail instanceof HTMLElement)) return;

    this.#setCurrent(index);

    scrollIntoView(targetThumbnail, {
      ancestor: thumbnails,
      behavior: options.behavior,
      block: 'center',
      inline: 'center',
    });

    const targetImage = media[index];

    if (targetImage) {
      targetImage.scrollIntoView({
        behavior: options.behavior,
      });
    }
    this.dispatchEvent(new ZoomMediaSelectedEvent(index));
  }
}

if (!customElements.get('zoom-dialog')) {
  customElements.define('zoom-dialog', ZoomDialog);
}

/**
 * @param {number} ms - How long to wait.
 * @returns {Promise<void>}
 */
function wait(ms) {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

/** @returns {ScrollBehavior} */
function scrollBehavior() {
  return prefersReducedMotion() ? 'instant' : 'smooth';
}

/**
 * The image box inside a media item, or the item itself when it has none.
 * @param {HTMLElement} item - A gallery item or slide.
 * @returns {HTMLElement}
 */
function mediaFrame(item) {
  const frame = item.querySelector('.product-media');
  return frame instanceof HTMLElement ? frame : item;
}

/**
 * Zooms in on the point under the pointer.
 * @param {HTMLElement} item - The media item.
 * @param {Element} frame - The image box.
 * @param {MouseEvent} event - The pointer event.
 */
function setZoomOrigin(item, frame, event) {
  const rect = frame.getBoundingClientRect();
  const x = Math.min(Math.max((event.clientX - rect.left) / rect.width, 0), 1) * 100;
  const y = Math.min(Math.max((event.clientY - rect.top) / rect.height, 0), 1) * 100;
  item.style.setProperty('--zoom-origin', `${x}% ${y}%`);
}

/**
 * Get the most visible element from a list of elements.
 * @param {HTMLElement[]} elements - The elements to get the most visible element from.
 * @returns {Promise<HTMLElement>} A promise that resolves to the most visible element.
 */
function getMostVisibleElement(elements) {
  return new Promise((resolve) => {
    const observer = new IntersectionObserver(
      (entries) => {
        const mostVisible = entries.reduce((prev, current) =>
          current.intersectionRatio > prev.intersectionRatio ? current : prev
        );
        observer.disconnect();
        resolve(/** @type {HTMLElement} */ (mostVisible.target));
      },
      {
        threshold: Array.from({ length: 100 }, (_, i) => i / 100),
      }
    );

    for (const element of elements) {
      observer.observe(element);
    }
  });
}
